import { useEffect, useState } from "react";
import * as Yup from "yup";
import { useFormik } from "formik";
import CustomButton from "shared/components/common/customButton";
import CustomInput from "shared/components/common/customInput";
import { Icons } from "assets";
import { toastMessage } from "shared/components/common/toast";
import styles from "./style.module.scss";
import classNames from "classnames";
import CustomFileUpload from "shared/components/common/customFileUpload";
import { CreateImageService, UpdateImageService } from "shared/services/images";
import ImageListings from "shared/components/dashboard/dashboardListings/imageListings";

const Images = () => {
  const [createUserMode, setCreateUserMode] = useState(false);
  const [imageId, setImageId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<string | null>(null);

  const validationSchema = Yup.object({
    title: Yup.string().required("Title is required"),
    desc: Yup.string().required("Description is required"),
    btnText: Yup.string().required("Button Text is required"),
    link: Yup.string().required("Button Link is required"),
    link2: Yup.string().required("Button Link is required"),
    image: Yup.mixed().required("Image is required"),
  });

  const validationSchema2 = Yup.object({
    title: Yup.string().required("Title is required"),
    desc: Yup.string().required("Description is required"),
    btnText: Yup.string().required("Button Text is required"),
    link: Yup.string().required("Button Link is required"),
    link2: Yup.string().required("Button Link is required"),
  });

  const formik = useFormik({
    initialValues: {
      title: "",
      desc: "",
      btnText: "",
      link: "",
      link2: "",
      image: null as File | null,
    },
    validationSchema: imageId ? validationSchema2 : validationSchema,
    onSubmit: async (values) => {
      try {
        setLoading(true);
        const formData = new FormData();

        formData.append("title", values.title);
        formData.append("desc", values.desc);
        formData.append("btnText", values.btnText);
        formData.append("link", values.link);
        formData.append("link2", values.link2);

        if (values.image) {
          formData.append("image", values.image);
        }

        if (imageId) {
          await UpdateImageService(imageId, formData);
          toastMessage("success", "Image updated successfully!");
          setImageId(null);
        } else {
          await CreateImageService(formData);
          toastMessage("success", "Image created successfully!");
          setImageId(null);
        }
        formik.resetForm();
        setCreateUserMode(false);
        setUploadedFile(null);
      } catch (error) {
        console.error("Error during user creation:", error);
        toastMessage("error", "Failed to create user.");
      } finally {
        setLoading(false);
      }
    },
  });

  const handleCreateEvent = () => {
    setCreateUserMode(true);
    formik.resetForm();
  };

  const handleCancel = () => {
    setCreateUserMode(false);
    setImageId(null);
  };

  const handleFileChange = (file: File | null) => {
    if (file) {
      formik.setFieldValue("image", file);
      setUploadedFile(URL.createObjectURL(file));
    }
  };

  // @ts-ignore
  const handleUpdate = (image) => {
    setCreateUserMode(true);
    setImageId(image.id);

    formik.setValues({
      title: image.title,
      desc: image.desc,
      btnText: image.btnText,
      link: image.link,
      link2: image.link2,
      image: null,
    });

    setUploadedFile(image.image.fullpath || null);
  };

  return (
    <div className={classNames(styles.sectionContainer)}>
      {createUserMode ? (
        <>
          <div>
            <CustomButton
              Icon={Icons.ArrowLeft}
              IconDirection="left"
              iconStyle="mr-2"
              title="Back"
              containerStyle="outlined-btn-primary mb-3 px-2"
              onClick={handleCancel}
            />
          </div>
          <form
            onSubmit={formik.handleSubmit}
            className={classNames(styles.body, "grid grid-cols-12 gap-2")}
          >
            <div className="col-span-6">
              <CustomInput
                label="Title"
                required
                placeholder="Enter title"
                name="title"
                value={formik.values.title}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.title && formik.errors.title
                    ? formik.errors.title
                    : ""
                }
              />
            </div>
            <div className="col-span-6">
              <CustomInput
                label="Description"
                required
                placeholder="Enter description"
                name="desc"
                value={formik.values.desc}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.desc && formik.errors.desc
                    ? formik.errors.desc
                    : ""
                }
              />
            </div>

            <div className="col-span-6">
              <CustomInput
                required
                label="Button Text"
                placeholder="Enter button text"
                name="btnText"
                value={formik.values.btnText}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.btnText && formik.errors.btnText
                    ? formik.errors.btnText
                    : ""
                }
              />
            </div>
            <div className="col-span-6">
              <CustomInput
                required
                label="Button Link"
                placeholder="Enter button link"
                name="link"
                value={formik.values.link}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.link && formik.errors.link
                    ? formik.errors.link
                    : ""
                }
              />
            </div>

            <div className="col-span-6">
              <CustomInput
                required
                label="Button Secondary Link"
                placeholder="Enter button link"
                name="link2"
                value={formik.values.link2}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.link2 && formik.errors.link2
                    ? formik.errors.link2
                    : ""
                }
              />
            </div>

            <div className="col-span-12">
              <CustomFileUpload
                label="Upload Image"
                required
                defaultImage={uploadedFile}
                // @ts-ignore
                onFileChange={handleFileChange}
              />
            </div>

            <div className="flex justify-end mt-3 w-full col-span-12">
              <CustomButton
                type="submit"
                loading={loading}
                title={imageId ? "Update Image" : "Add Image"}
              />
            </div>
          </form>
        </>
      ) : (
        <div className={classNames(styles.header)}>
          <div className="ms-auto w-fit">
            <CustomButton onClick={handleCreateEvent} title="Add Image" />
          </div>
          <div className="mt-4">
            <ImageListings handleUpdate={handleUpdate} />
          </div>
        </div>
      )}
    </div>
  );
};

export default Images;
